import clsx from 'clsx';
import React from 'react';
import Icon from '../../atoms/Icon/Icon';
import useUploadArea from './useUploadArea';

/**
 * UploadArea - Zona presentacional de subida de ficheros (drag & drop + selector nativo).
 *
 * Es un componente "tonto": solo gestiona el estado visual de drag-over, el input
 * oculto y el click para abrir el selector. Emite los ficheros crudos via
 * `onFilesSelected` sin validar ni mantener la lista seleccionada.
 */

export type UploadAreaProps = Readonly<{
  acceptedExtensions: string[];
  className?: string;
  isDisabled?: boolean;
  isMultiple?: boolean;
  maxSizeBytes: number;
  onFilesSelected: (files: File[]) => void;
}>;

function UploadArea({
  acceptedExtensions,
  className,
  isDisabled = false,
  isMultiple = true,
  maxSizeBytes,
  onFilesSelected,
}: UploadAreaProps): React.JSX.Element {
  const {
    acceptAttribute,
    allowedFormatsLabel,
    allowedFormatsText,
    browseText,
    dragAnnouncementText,
    dropTitleText,
    fileInputRef,
    handleClickSelect,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileInputChange,
    isDragOver,
    maxFileSizeLabel,
    maxFileSizeText,
  } = useUploadArea({ acceptedExtensions, isDisabled, maxSizeBytes, onFilesSelected });

  return (
    <div className={clsx('ui-upload-area', className)}>
      {/* Input oculto del arbol de accesibilidad: es solo el mecanismo que dispara el selector
          nativo. El control accesible es el boton "Browse" de mas abajo. */}
      <input
        ref={fileInputRef}
        accept={acceptAttribute}
        aria-hidden="true"
        className={clsx('ui-upload-area__input', 'sr-only')}
        disabled={isDisabled}
        multiple={isMultiple}
        tabIndex={-1}
        type="file"
        onChange={handleFileInputChange}
      />
      <div
        className={clsx('ui-upload-area__drag-container', {
          'ui-upload-area__drag-container--drag-over': isDragOver,
        })}
        role="presentation"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <span aria-live="polite" className="sr-only">
          {isDragOver ? dragAnnouncementText : ''}
        </span>
        <div className="ui-upload-area__drag-info">
          <span aria-hidden="true" className="ui-upload-area__drag-icon">
            <Icon color="primary" name="upload" size="xl" />
          </span>

          <div className="ui-upload-area__text-container">
            <span className="ui-upload-area__drag-title">{dropTitleText}</span>
            <button className="ui-upload-area__link" disabled={isDisabled} type="button" onClick={handleClickSelect}>
              {browseText}
            </button>
          </div>

          <div className="ui-upload-area__max-size">
            {maxFileSizeText}: {maxFileSizeLabel}
          </div>
          <div className="ui-upload-area__max-size">
            {allowedFormatsText}: {allowedFormatsLabel}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadArea;
